import { MetricId } from '../../../../../../api/types-graphql';
import { DATA_NOT_AVAILABLE_VALUE } from '../../../../../../constants';
import { formatMetricValue } from '../../../../../../utils-display';
import { TableData, TableViewRow } from '../../../../tableview/types';
import {
  ComponentStateManager,
  DataVizContainerCommonUtils,
} from '../../../common/types';
import { MetricTableVizData, MetricTableVizExternalProps } from '../../types';
import {
  MetricTableViewActions,
  MetricTableViewState,
  MetricTableViewUiComponentProps,
} from './types';

/**
 * Transforms the metric table data into TableData format for the TableView component.
 * Handles formatting of raw values here.
 */
const getTableDataFromMetricTableData = (
  data: MetricTableVizData
): TableData => {
  const { dataResponse, headers, metricDetails } = data;

  if (!dataResponse) {
    return {
      headers: [{ value: '' }, ...headers.map((h) => ({ value: h }))],
      rows: [],
    };
  }

  // First header is empty (for row labels column)
  const tableHeaders = [{ value: '' }, ...headers.map((h) => ({ value: h }))];

  const tableRows: TableViewRow[] = dataResponse.rows.map((row, rowIndex) => ({
    key: `row-${rowIndex}`,
    title: row.label,
    cells: row.cells.map((cell, cellIndex) => {
      // Format the value: use '-' for null, otherwise format based on metric type
      let formattedValue: string;
      if (cell.value === null) {
        formattedValue = DATA_NOT_AVAILABLE_VALUE;
      } else if (cell.metric) {
        const details = metricDetails[cell.metric.value as unknown as MetricId];
        formattedValue = details
          ? formatMetricValue(cell.value, details.formatType)
          : String(cell.value);
      } else {
        formattedValue = String(cell.value);
      }

      return {
        key: `cell-${rowIndex}-${cellIndex}`,
        value: formattedValue,
      };
    }),
  }));

  return {
    headers: tableHeaders,
    rows: tableRows,
  };
};

/**
 * Transforms metric table data into props for the UI component.
 */
export const metricTableViewDataToUiComponentProps = (
  data: MetricTableVizData,
  externalProps: MetricTableVizExternalProps,
  componentStateManager: ComponentStateManager<
    MetricTableViewState,
    MetricTableViewActions
  >,
  commonUtils: DataVizContainerCommonUtils
): MetricTableViewUiComponentProps => {
  const { fullScreenHandle } = externalProps;
  const { titleConfig } = data;

  const tableData = getTableDataFromMetricTableData(data);

  const displayConfig = {
    ...componentStateManager.state.displayState,
    ...componentStateManager.actions.displayActions,
  };

  return {
    fullScreenHandle,
    displayConfig,
    titleConfig,
    tableData,
  };
};
